import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { PoolStats } from '@/app/api/v1/poolStats/route';
import { assetByAddress, assetBySymbol } from '@/onchain/assets';
import { getPools } from '@/onchain/pools';
import { useAssetUSDPrices } from '@/onchain/hooks/useAssetUSDPrice';
import { getOrderedCurrenciesForPool } from '@/utils/tokenPreference';

export const QUERY_KEY_POOLS_INFO = (chainId: number, poolId?: string) => [
  'POOLS_INFO',
  chainId,
  poolId,
];

/**
 * Fetches pool stats for a given chain and pool ID
 * @param chainId - The chain ID to fetch the pool stats for
 * @param poolId - (optional) The pool ID to fetch the stats for
 * @returns The pool stats
 */
export function usePoolsInfo({ chainId, poolId }: { chainId: number; poolId?: string }) {
  const query = useSuspenseQuery({
    queryKey: QUERY_KEY_POOLS_INFO(chainId, poolId),
    queryFn: async () => {
      if (!chainId) {
        throw new Error('Chain ID is required');
      }

      const searchParams = new URLSearchParams({
        chainId: String(chainId),
      });
      if (poolId) {
        searchParams.append('poolId', poolId);
      }

      const url = `/api/v1/poolStats?${searchParams.toString()}`;
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch pool stats: ${resp.statusText}`);
      }

      const { pools: poolsDetails } = (await resp.json()) as { pools: PoolStats[] };

      let pools = getPools(chainId);
      if (poolId) {
        pools = pools.filter((pool) => pool.keyBytes32 === poolId);
      }

      // Gaurd for tEquity pools so we don't show them in unwanted environments.
      // Filter out pools that contain tokens not available in the current environment
      pools = pools.filter((pool) => {
        return pool.tokens.every((token) => assetBySymbol(token) !== undefined);
      });

      // Create a map of existing pool details by poolId
      const poolDetailsMap = poolsDetails.reduce(
        (acc, details) => {
          acc[details.poolId] = details;
          return acc;
        },
        {} as Record<string, PoolStats>,
      );

      // Map through all pools, using existing details or creating zero-filled details
      const poolsInfo = pools.map((pool) => {
        const poolDetails = poolDetailsMap[pool.keyBytes32] || {
          poolId: pool.keyBytes32,
          currencies: pool.tokens.map((token) => ({
            address: assetBySymbol(token)?.address[chainId],
            balance: '0',
            volume24h: '0',
            volume24hPrevious: '0',
            volume7d: '0',
            fees24h: '0',
            fees24hPrevious: '0',
            fees7d: '0',
          })),
        };

        const currencies = poolDetails.currencies.map(
          ({
            address,
            balance,
            volume24h,
            volume24hPrevious,
            volume7d,
            fees24h,
            fees24hPrevious,
            fees7d,
          }) => {
            const asset = assetByAddress(chainId, address) as NonNullable<
              ReturnType<typeof assetByAddress>
            >;
            const exponent = asset?.exponent ?? 0;
            return {
              ...asset,
              address,
              balance: Number.parseFloat(balance) / 10 ** exponent,
              volume24h: Number.parseFloat(volume24h) / 10 ** exponent,
              volume24hPrevious: Number.parseFloat(volume24hPrevious) / 10 ** exponent,
              volume7d: Number.parseFloat(volume7d) / 10 ** exponent,
              fees24h: Number.parseFloat(fees24h) / 10 ** exponent,
              fees24hPrevious: Number.parseFloat(fees24hPrevious) / 10 ** exponent,
              fees7d: Number.parseFloat(fees7d) / 10 ** exponent,
            };
          },
        );

        const { tokens, key } = pool;
        const { fee, hooks, tickSpacing } = key;

        // Use shared utility for consistent currency ordering
        const orderedCurrencies = getOrderedCurrenciesForPool(currencies);
        const baseToken = orderedCurrencies[0];
        const quoteToken = orderedCurrencies[1];
        const name = `${baseToken.displaySymbol} / ${quoteToken.displaySymbol}`;

        return {
          ...poolDetails,
          currencies,
          tokens,
          name,
          fee,
          hooks,
          tickSpacing,
        };
      });

      return poolsInfo;
    },
  });

  const tokenSymbols = useMemo(
    () =>
      query.data.flatMap(({ currencies }) => currencies.map((currency) => currency.displaySymbol)),
    [query.data],
  );

  const { data: prices } = useAssetUSDPrices(tokenSymbols);

  const poolsInfo = useMemo(
    () =>
      query.data.map((pool) => {
        const { currencies } = pool;
        const tvlNotional = pool.currencies.reduce((acc, currency) => {
          acc += currency.balance * prices[currency.displaySymbol];
          return acc;
        }, 0);

        const volume24hNotional = Math.max(
          ...currencies.map((currency) => currency.volume24h * prices[currency.displaySymbol]),
        );

        const volume24hPreviousNotional = Math.max(
          ...currencies.map(
            (currency) => currency.volume24hPrevious * prices[currency.displaySymbol],
          ),
        );

        const volume7dNotional = Math.max(
          ...currencies.map((currency) => currency.volume7d * prices[currency.displaySymbol]),
        );

        const fees24hNotional = currencies.reduce((acc, currency) => {
          acc += currency.fees24h * prices[currency.displaySymbol];
          return acc;
        }, 0);

        const fees24hPreviousNotional = currencies.reduce((acc, currency) => {
          acc += currency.fees24hPrevious * prices[currency.displaySymbol];
          return acc;
        }, 0);

        const fees7dNotional = currencies.reduce((acc, currency) => {
          acc += currency.fees7d * prices[currency.displaySymbol];
          return acc;
        }, 0);

        // Annualized based on 7 day fees
        const apr7dPercentage = tvlNotional > 0 ? (fees7dNotional / tvlNotional) * (365 / 7) : 0;

        return {
          ...pool,
          tvlNotional,
          volume24hNotional,
          volume24hPreviousNotional,
          volume7dNotional,
          fees24hNotional,
          fees24hPreviousNotional,
          fees7dNotional,
          apr7dPercentage,
        };
      }),
    [query.data, prices],
  );

  return {
    data: {
      poolsInfo,
      tokenPrices: prices,
    },
  };
}
